import type { Scene } from "@babylonjs/core/scene";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import type { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { CLOCKWORK_CANOPY_PLATFORMS } from "../layouts";

/** Visibility ownership around the existing bodies; never creates physics. */
export class CanopySurfaces {
  readonly bodies: AbstractMesh[];
  private readonly legacy: Array<{ mesh: AbstractMesh; visible: boolean }>;
  private readonly original: Array<{ mesh: AbstractMesh; visible: boolean; caster: boolean }>;
  private active = false;
  private retired = false;

  constructor(private readonly scene: Scene, private readonly shadows: ShadowGenerator) {
    const names = ["ground", ...CLOCKWORK_CANOPY_PLATFORMS.platforms.map(p => p.name),
      ...(CLOCKWORK_CANOPY_PLATFORMS.extraColliders ?? []).map(p => p.name)];
    this.bodies = names.map(name => {
      const mesh = scene.getMeshByName(name);
      if (!mesh) throw new Error(`Canopy physical surface missing: ${name}`);
      return mesh as AbstractMesh;
    });
    const casters = shadows.getShadowMap()?.renderList ?? [];
    this.original = this.bodies.map(mesh => ({ mesh, visible: mesh.isVisible, caster: casters.includes(mesh) }));
    this.legacy = scene.meshes.filter(m => names.some(name =>
      m.name === `${name}_frontBrassTrim` || m.name === `${name}_backBrassTrim`
        || m.name.startsWith(`${name}_trimRivet_`),
    )).map(mesh => ({ mesh: mesh as AbstractMesh, visible: mesh.isVisible }));
  }

  /** Refuse incomplete, translucent or misaligned standing skins atomically. */
  validate(meshes: readonly AbstractMesh[]): AbstractMesh[] {
    if (this.retired || this.scene.isDisposed) throw new Error("Canopy surfaces retired");
    return this.bodies.map(body => {
      const name = body.name === "ground" ? "Canopy_ground_top" : `CanopyDeck_${body.name}`;
      const matches = meshes.filter(m => m.name.replace(/^(Clone of )+/, "") === name);
      const skin = matches[0];
      if (matches.length !== 1 || !skin || skin.getTotalVertices() === 0 || !skin.isEnabled()
        || !skin.isVisible || skin.visibility !== 1 || !skin.material || skin.material.alpha !== 1
        || skin.material.needAlphaBlendingForMesh(skin) || skin.material.needAlphaTestingForMesh(skin)) {
        throw new Error(`Incomplete opaque Canopy surface: ${name}`);
      }
      body.computeWorldMatrix(true); skin.computeWorldMatrix(true);
      const b = body.getBoundingInfo().boundingBox, a = skin.getBoundingInfo().boundingBox;
      const delta = Math.max(...a.minimumWorld.asArray().map((v,i) => Math.abs(v - b.minimumWorld.asArray()[i])),
        ...a.maximumWorld.asArray().map((v,i) => Math.abs(v - b.maximumWorld.asArray()[i])));
      if (delta > 1e-5) throw new Error(`Canopy skin/body mismatch ${name}: ${delta}`);
      return skin;
    });
  }

  adopt(): void {
    if (this.retired || this.active || this.scene.isDisposed) throw new Error("Canopy surfaces unavailable");
    this.active = true;
    for (const { mesh } of this.original) { mesh.isVisible = false; this.shadows.removeShadowCaster(mesh); }
    for (const { mesh } of this.legacy) mesh.isVisible = false;
  }

  restore(): void {
    if (!this.active) return;
    this.active = false;
    for (const { mesh, visible, caster } of this.original) if (!mesh.isDisposed()) {
      mesh.isVisible = visible;
      if (caster && !this.scene.isDisposed) this.shadows.addShadowCaster(mesh);
    }
    for (const { mesh, visible } of this.legacy) if (!mesh.isDisposed()) mesh.isVisible = visible;
  }

  dispose(): void { this.restore(); this.retired = true; }
}
